import { AnyAction, createReducer } from '@reduxjs/toolkit';

import { AuthenticationContext } from '../types';

import {
	setIsAuthenticated,
	setIsInitializing,
	setUser,
	setToken,
	removeToken,
	setClient,
	setDefaultSite,
	setTeams,
	setLogoutFunction,
	setDefaultSiteName,
	injectProject,
	getProjectsResolved,
	getProjects,
	getProjectsRejected,
	getCompanies,
	getCompaniesRejected,
	getCompaniesResolved,
	getProjectBySourceId,
	getProjectBySourceIdResolved,
	getProjectBySourceIdRejected,
} from '../actions/authenticationContext.actions';
import { setUserContext } from '../actions/userContext.actions';
import { clearCache } from '../actions/app.actions';
import {
	userTeamAccessVerifyRejected,
	userTeamAccessVerifyResolved,
} from '../actions/userTeamAccess.actions';

export const initialState: AuthenticationContext = {
	isAuthenticated: false,
	isInitializing: true,
	teams: [],
	projects: {
		isInitialized: false,
		isFetching: false,
		isProjectByIdInitialized: false,
		error: null,
		cursor: null,
		list: [],
	},
	companies: {
		isInitialized: false,
		isFetching: false,
		error: null,
		list: [],
	},
	defaultSite: '',
	defaultSiteName: '',
	defaultSiteProjectId: '',
	defaultTeamId: '',
};

interface AuthenticationContextReducer {
	[x: string]: (
		state: AuthenticationContext,
		action: AnyAction,
	) => AuthenticationContext | undefined;
}

const reducer: AuthenticationContextReducer = {
	[setIsAuthenticated.toString()]: (state, action) => ({
		...state,
		isAuthenticated: action.payload,
	}),
	[setIsInitializing.toString()]: (state, action) => ({
		...state,
		isInitializing: action.payload,
	}),
	[userTeamAccessVerifyResolved.toString()]: state => ({
		...state,
		isInitializing: false,
	}),
	[userTeamAccessVerifyRejected.toString()]: state => ({
		...state,
		isInitializing: false,
	}),
	[setUser.toString()]: (state, action) => ({
		...state,
		user: action.payload,
	}),
	[setUserContext.toString()]: (state, action) => ({
		...state,
		defaultSiteProjectId: action.payload.defaultSiteProjectId,
		defaultTeamId: action.payload.defaultTeamId,
	}),
	[getCompanies.toString()]: (state, action) => ({
		...state,
		companies: {
			...state.companies,
			isInitialized: !action.payload.initializing,
			isFetching: true,
		},
	}),
	[getCompaniesResolved.toString()]: (state, action) => ({
		...state,
		companies: {
			...state.companies,
			list: action.payload,
			isInitialized: true,
			isFetching: false,
			error: null,
		},
	}),
	[getCompaniesRejected.toString()]: (state, action) => ({
		...state,
		companies: {
			...state.companies,
			isInitialized: false,
			isFetching: false,
			error: action.payload,
		},
	}),
	[getProjects.toString()]: (state, action) => ({
		...state,
		projects: {
			...state.projects,
			isInitialized: !action.payload.initializing,
			isFetching: true,
		},
	}),
	[getProjectsResolved.toString()]: (state, action) => ({
		...state,
		projects: {
			...state.projects,
			list: [...action.payload.list],
			cursor: action.payload.cursor,
			isInitialized: true,
			isFetching: false,
			error: null,
		},
	}),
	[getProjectsRejected.toString()]: (state, action) => ({
		...state,
		projects: {
			...state.projects,
			isInitialized: true,
			isFetching: false,
			error: action.payload,
		},
	}),
	[getProjectBySourceId.toString()]: state => ({
		...state,
		projects: {
			...state.projects,
			isFetching: true,
		},
	}),
	[getProjectBySourceIdResolved.toString()]: (state, action) => ({
		...state,
		projects: {
			...state.projects,
			isFetching: false,
			isProjectByIdInitialized: true,
			list: [...state.projects.list, action.payload],
		},
	}),
	[getProjectBySourceIdRejected.toString()]: (state, action) => ({
		...state,
		projects: {
			...state.projects,
			isFetching: false,
			isProjectByIdInitialized: true,
			error: action.payload,
		},
	}),
	[setToken.toString()]: (state, action) => ({
		...state,
		token: action.payload,
	}),
	[removeToken.toString()]: (state, action) => ({
		...state,
		token: undefined,
	}),
	[setDefaultSite.toString()]: (state, action) => ({
		...state,
		defaultSite: action.payload,
	}),
	[setDefaultSiteName.toString()]: (state, action) => ({
		...state,
		defaultSiteName: action.payload,
	}),
	[setClient.toString()]: (state, action) => ({
		...state,
		client: action.payload,
	}),
	[setTeams.toString()]: (state, action) => ({
		...state,
		teams: action.payload.map(({ sourceId, ...team }) => ({
			...team,
			projectSourceId: sourceId || undefined,
		})),
	}),
	[setLogoutFunction.toString()]: (state, action) => ({
		...state,
		logout: action.payload,
	}),
	[injectProject.toString()]: (state, { payload: { name, id } }) => ({
		...state,
		defaultSiteProjectId: id,
		defaultSiteName: name,
	}),
	[clearCache.toString()]: () => ({ ...initialState }),
};

export default createReducer(initialState, reducer);
